import { DocumentLinkPopover } from '@/shared/components/projects/project-profile/DocumentLinkPopover'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { FolderSelection } from '@/shared/components/FolderSelection'
import { AssignTaskDialog } from '@/shared/components/legal-documents/AssignTaskDialog'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { api, StatusType, type Task, TaskPhase } from '@/shared/lib/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { z } from 'zod'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Badge } from '@workspace/ui/components/Badge'
import { BsSelect } from '@workspace/ui/components/Select'
import { Button } from '@workspace/ui/components/Button'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import MenuDropdown, { type MenuAction } from '@/shared/components/MenuDropdown'
import { Tabs, TabList, Tab, TabPanel } from '@workspace/ui/components/Tabs'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { MoreHorizontal, PlusIcon, FileSpreadsheet, LayoutDashboard, FileText } from 'lucide-react'
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import dayjs from 'dayjs'
import { toast } from '@workspace/ui/components/Sonner'
import { useRegions } from '@/shared/hooks/useRegions'
import { useAreas } from '@/shared/hooks/useAreas'
import { useCan } from '@/shared/lib/authorization'
import { TaskDetailDialog } from '@/shared/components/legal-documents/TaskDetailDialog'
import { PreviewSearchItem } from '@/shared/components/previewSearchItem'
import { WeeklyReportFormDialog, WeeklyReportDashboardDialog } from '@/shared/components/weekly-reports'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { useUser } from '@/shared/store/auth.store'

export const Route = createFileRoute('/legal-documents/tasks/')({
    validateSearch: z.object({
        taskId: z.string().optional(),
        searchId: z.string().optional(),
        type: z.string().optional(),
        documentId: z.string().optional(),
        q: z.string().optional(),
        page: z.number().catch(1).optional(),
        pageSize: z.number().catch(10).optional(),
        regionValues: z.string().optional(),
        areaValues: z.string().optional(),
        statusValues: z.string().optional(),
        projectValues: z.string().optional(),
        phaseValues: z.string().optional(),
        priorityValues: z.string().optional(),
        assigneeValues: z.string().optional(),
        textFormatValues: z.string().optional(),
        activeTab: z.string().optional(),
        sortBy: z.string().optional(),
        sortDirection: z.string().optional(),
        openWeeklyReport: z.boolean().catch(false).optional(),
        week: z.number().catch(0).optional(),
        year: z.number().catch(0).optional(),
    }),
    component: LegalDocumentTasksPage,
    beforeLoad: requirePermissionBeforeLoad('view', 'task'),
})

const columnHelper = createColumnHelper<Task>()

function LegalDocumentTasksPage() {
    const navigate = useNavigate({ from: Route.fullPath })
    const searchParams = Route.useSearch()
    const { t } = useTranslation()
    const [rowSelection, setRowSelection] = useState({})
    const [isWeeklyReportFormOpen, setIsWeeklyReportFormOpen] = useState(false)
    const [isWeeklyReportDashboardOpen, setIsWeeklyReportDashboardOpen] = useState(false)
    const user = useUser()
    const isSuperAdmin = user?.role?.name === ADMIN_ROLE_NAME

    const searchQuery = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const regionValues = searchParams.regionValues ? searchParams.regionValues.split(',') : []
    const areaValues = searchParams.areaValues ? searchParams.areaValues.split(',') : []
    const statusValues = searchParams.statusValues ? searchParams.statusValues.split(',') : []
    const priorityValues = searchParams.priorityValues ? searchParams.priorityValues.split(',') : []
    const assigneeValues = searchParams.assigneeValues ? searchParams.assigneeValues.split(',') : []
    const textFormatValues = searchParams.textFormatValues ? searchParams.textFormatValues.split(',') : []
    const projectValues = searchParams.projectValues || null
    const phaseValues = (searchParams.phaseValues as string) || null
    const activeTab = (searchParams.activeTab as 'all' | 'assigned_to_me' | 'assigned_by_me') || 'all'
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: (searchParams.sortDirection as 'asc' | 'desc') || 'asc' }
        : null

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }), replace: true })
    }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setRegionValues = (values: string[]) => {
        if (values.length === 0) {
            updateSearch({ regionValues: undefined, areaValues: undefined, page: 1 })
        } else {
            updateSearch({ regionValues: values.join(','), page: 1 })
        }
    }
    const setAreaValues = (values: string[]) =>
        updateSearch({ areaValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setStatusValues = (values: string[]) =>
        updateSearch({ statusValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setPriorityValues = (values: string[]) =>
        updateSearch({ priorityValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setAssigneeValues = (values: string[]) =>
        updateSearch({ assigneeValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setTextFormatValues = (values: string[]) =>
        updateSearch({ textFormatValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setProjectValues = (value: string | null) => {
        if (!value) {
            updateSearch({ projectValues: undefined, phaseValues: undefined, page: 1 })
        } else {
            updateSearch({ projectValues: value, page: 1 })
        }
    }
    const setPhaseValues = (value: string | null) => updateSearch({ phaseValues: value || undefined, page: 1 })
    const setActiveTab = (value: string) => updateSearch({ activeTab: value === 'all' ? undefined : value, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [isAssignTaskOpen, setIsAssignTaskOpen] = useState(false)
    const [selectedTask, setSelectedTask] = useState<Task | null>(null)
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false)
    const [assignTaskMode, setAssignTaskMode] = useState<'assignment' | 'order'>('assignment')

    const { taskId } = Route.useSearch()

    // Fetch single task if taskId is provided in URL
    const { data: detailTask, isSuccess: isDetailSuccess } = useQuery({
        queryKey: ['task', taskId],
        queryFn: () => api.task.findOne(taskId!),
        enabled: !!taskId,
    })

    useEffect(() => {
        if (isDetailSuccess && detailTask) {
            setSelectedTask(detailTask)
            setIsTaskDetailOpen(true)
            // Clear taskId from search params to keep URL clean
            navigate({
                search: (prev: any) => {
                    const { taskId: _, ...rest } = prev
                    return rest
                },
                replace: true,
            })
        }
    }, [isDetailSuccess, detailTask, navigate])

    const [dashboardDefaultWeek, setDashboardDefaultWeek] = useState<number | undefined>()
    const [dashboardDefaultYear, setDashboardDefaultYear] = useState<number | undefined>()

    const { openWeeklyReport, week, year } = Route.useSearch()

    useEffect(() => {
        if (openWeeklyReport) {
            if (week && year) {
                setDashboardDefaultWeek(week)
                setDashboardDefaultYear(year)
            }
            setIsWeeklyReportDashboardOpen(true)

            navigate({
                search: (prev: any) => {
                    const { openWeeklyReport: _, week: __, year: ___, ...rest } = prev
                    return rest
                },
                replace: true,
            })
        }
    }, [openWeeklyReport, week, year, navigate])

    const [isRegionFilterOpen, setIsRegionFilterOpen] = useState(false)
    const [isProjectSelectOpen, setIsProjectSelectOpen] = useState(false)
    const [isAreaFilterOpen, setIsAreaFilterOpen] = useState(false)
    const [isAssigneeFilterOpen, setIsAssigneeFilterOpen] = useState(false)

    const debouncedSearch = useDebounce(searchQuery, 300)

    const handleDeleteMultiple = async () => {
        const taskIds = Object.keys(rowSelection)
        if (taskIds.length === 0) return

        await deleteMutation.mutateAsync(taskIds)
    }

    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            'tasks',
            debouncedSearch,
            regionValues,
            areaValues,
            statusValues,
            projectValues,
            phaseValues,
            priorityValues,
            assigneeValues,
            textFormatValues,
            activeTab,
            currentPage,
            pageSize,
            sorting,
        ],
        queryFn: () =>
            api.task.list({
                page: currentPage,
                take: pageSize,
                q: debouncedSearch || undefined,
                status:
                    statusValues.filter(s => s !== 'overdue').length > 0
                        ? (statusValues.filter(s => s !== 'overdue').join(',') as StatusType)
                        : undefined,
                isOverdue: statusValues.includes('overdue') ? true : undefined,
                regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
                areaIds: areaValues.length > 0 ? areaValues.join(',') : undefined,
                priorities: priorityValues.length > 0 ? priorityValues.join(',') : undefined,
                taskType: textFormatValues.length > 0 ? textFormatValues.join(',') : undefined,
                assignedTo: assigneeValues.length > 0 ? assigneeValues.join(',') : undefined,
                tab: activeTab === 'all' ? undefined : activeTab,
                phaseId: phaseValues,
                projectId: projectValues,
                orderBy: sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }),
    })

    // Delete task mutation
    const deleteMutation = useMutation({
        mutationFn: async (taskIds: string[]) => {
            console.log(taskIds)

            // Delete tasks in parallel
            await Promise.all(taskIds.map(id => api.task.delete(id)))
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('assignTask.list.deleteSuccess.description'),
            })
            // Refresh the list and clear selection
            refetch()
            setRowSelection({})
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('assignTask.list.deleteError.description'),
            })
            console.error('Delete error:', error)
        },
    })

    const statusOptions = [
        { id: 'todo', name: 'Chuẩn bị công việc' },
        { id: 'receiving', name: 'Kiểm tra hồ sơ & tiếp nhận công việc' },
        { id: 'in_progress', name: 'Đang xử lý' },
        { id: 'paused', name: 'Tạm dừng' },
        { id: 'waiting_approval', name: 'Chờ duyệt' },
        { id: 'completed', name: 'Hoàn thành' },
        { id: 'cancelled', name: 'Đã hủy' },
        { id: 'overdue', name: 'Quá hạn' },
    ]

    const textFormatOptions = [
        { id: 'order', name: 'Đặt hàng' },
        { id: 'assignment', name: 'Giao việc' },
    ]

    const priorityOptions = [
        { id: 'high', name: 'Cao' },
        { id: 'medium', name: 'Trung bình' },
        { id: 'low', name: 'Thấp' },
    ]

    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('')
    const debouncedAssigneeSearch = useDebounce(assigneeSearchQuery, 300)

    // Fetch users for manager filter dropdown
    const { data: usersData, isLoading: isLoadingAssignees } = useQuery({
        queryKey: ['users', debouncedAssigneeSearch],
        queryFn: () => api.user.listAll({ page: 1, take: 100, q: debouncedAssigneeSearch || undefined }),
        enabled: isAssigneeFilterOpen,
    })

    const assigneeOptions = usersData?.data.map(user => ({ id: user.id, name: user.fullName })) || []

    const { data: projects, isLoading: isLoadingProjects } = useQuery({
        queryKey: ['projects'],
        queryFn: () => api.project.list({ page: 1, take: 100 }),
        enabled: isProjectSelectOpen,
    })

    const { data: regions, isLoading: isLoadingRegions } = useRegions({
        enabled: isRegionFilterOpen,
    })

    const { data: allAreasData, isLoading: isLoadingAreas } = useAreas({
        enabled: true,
    })

    const regionOptions = (regions?.data || []).map(region => ({ id: region.id, name: region.name }))

    const areaOptions = useMemo(() => {
        const allAreas = allAreasData?.data || []
        if (regionValues.length === 0) {
            return allAreas.map((area: any) => ({ id: area.id, name: area.name }))
        }
        return allAreas
            .filter((area: any) => regionValues.includes(area.region?.id))
            .map((area: any) => ({ id: area.id, name: area.name }))
    }, [allAreasData, regionValues])

    // Auto-prune areaValues when regionValues change
    useEffect(() => {
        if (!allAreasData?.data || areaValues.length === 0 || regionValues.length === 0) return

        const validAreaIds = new Set(
            allAreasData.data.filter((a: any) => regionValues.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaValues = areaValues.filter(id => validAreaIds.has(id))

        if (newAreaValues.length !== areaValues.length) {
            updateSearch({
                areaValues: newAreaValues.length > 0 ? newAreaValues.join(',') : undefined,
                page: 1,
            })
        }
    }, [regionValues, areaValues, allAreasData])

    const projectOptions = projects?.data.map(project => ({ name: project.name || '', id: project.id })) || []

    const totalResults = data?.meta?.itemCount || 0

    const columns = [
        columnHelper.display({
            id: 'custom-actions',
            header: '',
            size: 48,
            enableSorting: false,
            meta: { className: '!max-w-[48px] !min-w-[48px] !w-[48px]' },
            cell: ({ row }) => {
                const task = row.original
                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground size-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                <MenuItem
                                    onAction={() => {
                                        setSelectedTask(task)
                                        setIsTaskDetailOpen(true)
                                    }}
                                    className="cursor-pointer"
                                >
                                    {t('common.actions.view')}
                                </MenuItem>
                                {task.canDelete && (
                                    <MenuItem
                                        onAction={() =>
                                            confirm({
                                                title: t('assignTask.list.deleteConfirm.title'),
                                                description: t('assignTask.list.deleteConfirm.description'),
                                                action: {
                                                    label: t('common.actions.delete'),
                                                    onClick: async () => {
                                                        await deleteMutation.mutateAsync([task.id])
                                                    },
                                                },
                                                cancel: {
                                                    label: t('common.actions.cancel'),
                                                },
                                                variant: 'destructive',
                                            })
                                        }
                                        className="cursor-pointer text-destructive"
                                    >
                                        {t('common.actions.delete')}
                                    </MenuItem>
                                )}
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('taskCode', {
            header: 'Mã công việc',
            enableSorting: false,
            size: 120,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const task = row.original
                const projectCode = task.project?.code
                return (
                    <>
                        <div className="text-foreground truncate">{task.taskCode}</div>
                        {projectCode && (
                            <div className="text-xs text-neutral-500 truncate" title={projectCode}>
                                {projectCode}
                            </div>
                        )}
                    </>
                )
            },
        }),
        columnHelper.accessor('title', {
            header: 'Công việc',
            enableSorting: true,
            size: 320,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const task = row.original
                const projectName = task.project?.name
                const relatedDocTitle = task.relatedDocuments?.[0]?.title

                let projectPath = null
                if (projectName) {
                    if (relatedDocTitle) {
                        projectPath = `${projectName} /... / ${relatedDocTitle}`
                    } else {
                        projectPath = projectName
                    }
                }

                return (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => {
                            setSelectedTask(task)
                            setIsTaskDetailOpen(true)
                        }}
                    >
                        <div className="flex-1 min-w-0 flex flex-col">
                            <span className="text-sm text-foreground truncate" title={task.title || 'N/A'}>
                                {task.title || 'N/A'}
                            </span>
                            {projectPath && (
                                <span
                                    className="text-xs text-neutral-500 truncate max-w-full w-fit"
                                    title={projectPath}
                                >
                                    {projectPath}
                                </span>
                            )}
                        </div>
                        <DocumentLinkPopover
                            relatedTasks={[
                                ...(task?.relatedTasks || []),
                                ...(task.parentTask ? [task.parentTask] : []),
                            ]}
                            onViewTaskDetail={task => {
                                setSelectedTask(task)
                                setIsTaskDetailOpen(true)
                            }}
                        />
                    </div>
                )
            },
        }),
        columnHelper.accessor('taskType', {
            header: 'Hình thức',
            enableSorting: false,
            size: 90,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const value = row.original.taskType
                const labelMap: Record<string, string> = {
                    assignment: 'Giao việc',
                    order: 'Đặt hàng',
                }
                return <span title={labelMap[value || 'order']}>{labelMap[value || 'order']}</span>
            },
        }),
        columnHelper.display({
            id: 'region-area',
            header: 'Vùng - Khu vực',
            enableSorting: false,
            size: 130,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const region = row.original.regions?.map(r => r.name).filter(Boolean) || []
                const area = row.original.areas?.map(a => a.name).filter(Boolean) || []
                return (
                    <div className="flex flex-col py-1">
                        <span className="text-sm font-medium text-foreground leading-5 truncate">
                            {region.join(', ')}
                        </span>
                        <span className="text-xs text-muted-foreground leading-4 truncate">{area.join(', ')}</span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'priority',
            header: 'Mức ưu tiên',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const priority = row.original.priority

                const priorityConfig: Record<
                    'high' | 'medium' | 'low',
                    { backgroundColor: string; textColor: string }
                > = {
                    high: {
                        backgroundColor: 'var(--color-red-500)',
                        textColor: 'white',
                    },
                    medium: {
                        backgroundColor: 'var(--primary)',
                        textColor: 'white',
                    },
                    low: {
                        backgroundColor: 'var(--color-green-500)',
                        textColor: 'white',
                    },
                }

                const config = priorityConfig[priority]
                const priorityLabel: Record<'high' | 'medium' | 'low', string> = {
                    high: 'Cao',
                    medium: 'Trung bình',
                    low: 'Thấp',
                }

                return (
                    <Badge
                        className="h-5 px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                            backgroundColor: config.backgroundColor,
                            color: config.textColor,
                        }}
                    >
                        {priorityLabel[priority]}
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'createdBy',
            header: 'Người giao',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const creator = row.original.createdBy
                if (!creator) return <span className="text-xs text-muted-foreground text-center pl-3">-</span>

                return (
                    <div className="flex items-center gap-2">
                        <UserAvatar
                            src={creator.avatar}
                            alt={creator.fullName}
                            fallback={creator.fullName}
                            size="md"
                            className="border-2 border-white"
                        />
                        <span className="text-sm text-foreground truncate max-w-[60px]" title={creator.fullName}>
                            {creator.fullName}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'assignedUsers',
            header: 'Phân công',
            enableSorting: false,
            size: 100,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const assignees = row.original.assignedUsers
                if (!assignees || assignees.length === 0)
                    return <span className="text-xs text-muted-foreground text-center pl-3">-</span>

                return (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center -space-x-2 shrink-0">
                            {assignees.slice(0, 2).map(assignee => (
                                <UserAvatar
                                    key={assignee.id}
                                    src={assignee.avatar}
                                    alt={assignee.fullName}
                                    fallback={assignee.fullName}
                                    size="md"
                                    className="border-2 border-white"
                                />
                            ))}
                            {assignees.length > 2 && (
                                <div
                                    className="relative z-10 size-8 rounded-full bg-gray-110 flex items-center justify-center border-2 border-background"
                                    title={assignees
                                        .slice(2)
                                        .map(a => a.fullName)
                                        .join(', ')}
                                >
                                    <span className="text-xs text-gray-850 font-semibold">+{assignees.length - 2}</span>
                                </div>
                            )}
                        </div>
                        <span className="text-sm text-foreground truncate max-w-[60px]" title={assignees[0].fullName}>
                            {assignees[0].fullName}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'status',
            header: 'Trạng thái',
            enableSorting: false,
            size: 155,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head text-center' },
            cell: ({ row }) => {
                const status = row.original.status

                const statusConfig: Record<
                    | 'todo'
                    | 'receiving'
                    | 'in_progress'
                    | 'paused'
                    | 'waiting_approval'
                    | 'completed'
                    | 'cancelled'
                    | 'overdue'
                    | 'returned',
                    { backgroundColor: string; textColor: string }
                > = {
                    todo: {
                        backgroundColor: 'var(--color-gray-400)',
                        textColor: 'white',
                    },
                    receiving: {
                        backgroundColor: 'var(--color-blue-500)',
                        textColor: 'white',
                    },
                    in_progress: {
                        backgroundColor: 'var(--color-primary)',
                        textColor: 'white',
                    },
                    paused: {
                        backgroundColor: 'var(--color-orange-500)',
                        textColor: 'white',
                    },
                    waiting_approval: {
                        backgroundColor: 'var(--color-indigo-500)',
                        textColor: 'white',
                    },
                    completed: {
                        backgroundColor: 'var(--color-green-500)',
                        textColor: 'white',
                    },
                    cancelled: {
                        backgroundColor: 'var(--color-gray-700)',
                        textColor: 'white',
                    },
                    overdue: {
                        backgroundColor: 'var(--color-red-500)',
                        textColor: 'white',
                    },
                    returned: {
                        backgroundColor: 'var(--color-slate-300)',
                        textColor: 'white',
                    },
                }

                const config = statusConfig[status as keyof typeof statusConfig]
                const statusLabel = t(`assignTask.statuses.${status}`, status)

                return (
                    <Badge
                        className="h-5 px-2 py-0.5 text-xs font-medium rounded-full max-w-[135px]"
                        style={{
                            backgroundColor: config?.backgroundColor || 'var(--color-gray-400)',
                            color: config?.textColor || 'white',
                        }}
                    >
                        <span className="truncate" title={statusLabel}>
                            {statusLabel}
                        </span>
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'progressPercent',
            header: 'Tiến trình',
            enableSorting: false,
            size: 160,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row }) => {
                const progress = Math.round(row.original.progressPercent || 0)
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-border rounded-md overflow-hidden">
                            <div
                                className="h-full bg-background-muted rounded-md transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="text-sm text-foreground w-10 text-left">{progress}%</span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('dueDate', {
            header: 'Hạn cuối',
            enableSorting: true,
            size: 140,
            meta: { headerClassName: 'font-bold text-sm text-text-table-head' },
            cell: ({ row, getValue }) => {
                const value = getValue()
                const isOverdue = row.original.isOverdue
                const dueDate = dayjs(value).format('DD/MM/YYYY')
                return (
                    <span
                        className={`text-sm ${isOverdue ? 'text-red-500 font-semibold' : 'text-foreground'}`}
                        title={dueDate}
                    >
                        {value ? dueDate : '-'}
                    </span>
                )
            },
        }),
    ]
    const canOperateTask = useCan('operate', 'task')
    const canOperateWeeklyReport = isSuperAdmin || useCan('operate', 'weekly_report')
    const canViewWeeklyReport = isSuperAdmin || useCan('view', 'weekly_report')
    const hasWeeklyReportAccess = canOperateWeeklyReport || canViewWeeklyReport

    const weeklyReportActions = useMemo<MenuAction[]>(() => {
        const actions: MenuAction[] = []
        if (canOperateWeeklyReport) {
            actions.push({
                label: 'Nộp báo cáo tuần',
                onAction: () => setIsWeeklyReportFormOpen(true),
                icon: <FileSpreadsheet size={16} />,
            })
        }
        if (canViewWeeklyReport) {
            actions.push({
                label: 'Quản lý báo cáo tuần',
                onAction: () => setIsWeeklyReportDashboardOpen(true),
                icon: <LayoutDashboard size={16} />,
            })
        }
        return actions
    }, [canOperateWeeklyReport, canViewWeeklyReport])

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 max-lg:p-0 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between max-lg:flex-col max-lg:items-start max-lg:gap-2 shrink-0">
                <h1 className="text-2xl font-bold">Danh mục pháp lý (Công việc)</h1>
                <div className="flex max-sm:flex-wrap items-center gap-4 max-lg:gap-2 max-lg:w-full">
                    {hasWeeklyReportAccess && (
                        <MenuDropdown
                            actions={weeklyReportActions}
                            children={
                                <Button
                                    variant="default"
                                    className="bg-background-accent text-text-accent hover:bg-background-accent/90 max-lg:w-full"
                                >
                                    Báo cáo tuần
                                </Button>
                            }
                        />
                    )}
                    <Button
                        variant="outline"
                        className="gap-2 border-border-medium px-3 max-lg:w-full"
                        hidden={!useCan('operate', 'task')}
                        onPress={() => {
                            setAssignTaskMode('order')
                            setIsAssignTaskOpen(true)
                        }}
                    >
                        Đặt hàng công việc
                    </Button>
                    <Button
                        variant="default"
                        className="gap-2 bg-background-accent text-text-accent hover:bg-background-accent/90 max-lg:w-full"
                        hidden={!useCan('operate', 'task')}
                        onPress={() => {
                            setAssignTaskMode('assignment')
                            setIsAssignTaskOpen(true)
                        }}
                    >
                        <PlusIcon size={20} strokeWidth={1.5} />
                        Giao việc
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-4 flex-wrap shrink-0">
                <Tabs className="shrink-0" selectedKey={activeTab} onSelectionChange={k => setActiveTab(k as any)}>
                    <TabList className="w-fit">
                        <Tab id="all">Tất cả</Tab>
                        <Tab id="assigned_to_me">Công việc được giao</Tab>
                        <Tab id="assigned_by_me">Công việc đã giao</Tab>
                    </TabList>
                </Tabs>

                <div className="flex gap-4 flex-wrap max-lg:w-full max-lg:gap-2 shrink-0">
                    <BsSelect
                        options={projectOptions}
                        selectionMode="single"
                        value={projectValues}
                        onChange={key => setProjectValues(key ? (key as string) : null)}
                        placeholder="Dự án"
                        className="w-80 max-lg:w-full"
                        btnClassName="h-10 rounded-[8px]"
                        popoverClassName="w-full max-w-[400px]"
                        isClearable
                        isSearchable
                        isLoading={isLoadingProjects}
                        setOpen={setIsProjectSelectOpen}
                    />
                    <FolderSelection
                        projectId={projectValues || undefined}
                        value={phaseValues}
                        onChange={key => setPhaseValues(key ? (key as string) : null)}
                        placeholder="Giai đoạn"
                        className="w-50 max-lg:w-full"
                        btnClassName="h-10 rounded-[8px]"
                        popoverClassName="w-full max-w-[300px]"
                        isClearable
                        getOnlyParent
                        enabled={!!projectValues}
                        isDisabled={!projectValues}
                        disabledTooltip="Vui lòng chọn Dự án trước"
                    />
                </div>
            </div>

            {/* Search and Filters */}
            <div className="flex gap-4 flex-wrap max-lg:gap-2 shrink-0">
                <BsSearchField
                    className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                    containerClassName="max-lg:w-full"
                    placeholder="Tìm kiếm"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    autoTrim
                />

                <FilterDropdown
                    placeholder={t('common.fields.textFormat')}
                    inputPlaceholder={t('common.fields.textFormat')}
                    options={textFormatOptions}
                    selectedValues={textFormatValues}
                    onChange={setTextFormatValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('common.fields.region')}
                    inputPlaceholder={t('common.fields.region')}
                    options={regionOptions}
                    selectedValues={regionValues}
                    onChange={setRegionValues}
                    setOpen={setIsRegionFilterOpen}
                    isLoading={isLoadingRegions}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('common.fields.area')}
                    inputPlaceholder={t('common.fields.area')}
                    options={areaOptions}
                    selectedValues={areaValues}
                    onChange={setAreaValues}
                    setOpen={setIsAreaFilterOpen}
                    isLoading={isLoadingAreas}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                    isDisabled={!regionValues || regionValues.length === 0}
                    disabledTooltip={t('common.filters.requireRegionFirst')}
                />
                <FilterDropdown
                    placeholder={t('assignTask.labels.priorityLevel')}
                    inputPlaceholder={t('assignTask.labels.priorityLevel')}
                    options={priorityOptions}
                    selectedValues={priorityValues}
                    onChange={setPriorityValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('assignTask.labels.assignees')}
                    inputPlaceholder={t('assignTask.labels.assignees')}
                    options={assigneeOptions}
                    selectedValues={assigneeValues}
                    onChange={setAssigneeValues}
                    onSearchChange={setAssigneeSearchQuery}
                    setOpen={setIsAssigneeFilterOpen}
                    isLoading={isLoadingAssignees}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('common.fields.status')}
                    inputPlaceholder={t('common.fields.status')}
                    options={statusOptions}
                    selectedValues={statusValues}
                    onChange={setStatusValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
            </div>

            <DataTable
                data={data?.data || []}
                columns={columns}
                enableRowSelection={row =>
                    canOperateTask || !!(row.original as Task & { canDelete?: boolean }).canDelete
                }
                rowSelection={rowSelection}
                enableSorting={true}
                sorting={sorting}
                setSorting={setSorting}
                setRowSelection={setRowSelection}
                isLoading={isLoading}
                emptyString={t('common.emptyState')}
                getRowClassName={row => (row.original.isOverdue ? 'bg-background-overdue' : undefined)}
                containerClassName="flex-1 overflow-y-auto min-h-0"
            />

            <div className="shrink-0">
                <TableDataFooter
                    selectedCount={Object.keys(rowSelection).length}
                    onDelete={handleDeleteMultiple}
                    deleteDisabled={Object.keys(rowSelection).length === 0 || deleteMutation.isPending}
                    totalCount={totalResults}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalPages={Math.ceil(totalResults / pageSize)}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={value => setPageSize(value || 10)}
                />
            </div>

            <AssignTaskDialog open={isAssignTaskOpen} onOpenChange={setIsAssignTaskOpen} mode={assignTaskMode} />
            {selectedTask && (
                <TaskDetailDialog
                    open={isTaskDetailOpen}
                    onOpenChange={setIsTaskDetailOpen}
                    taskId={selectedTask.id}
                    onNavigateToTask={setSelectedTask}
                    taskType={selectedTask.taskType}
                />
            )}

            <WeeklyReportFormDialog open={isWeeklyReportFormOpen} onOpenChange={setIsWeeklyReportFormOpen} />
            <WeeklyReportDashboardDialog
                open={isWeeklyReportDashboardOpen}
                onOpenChange={setIsWeeklyReportDashboardOpen}
                defaultWeek={dashboardDefaultWeek}
                defaultYear={dashboardDefaultYear}
            />

            <PreviewSearchItem />
        </div>
    )
}
